import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Phone, Mail, Send, ArrowLeft, Building2 } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { toast } from 'sonner';

export default function ContactPage() {
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', { name: name.trim(), email: email.trim(), message: message.trim() });
      toast.success("Message sent. We'll be in touch.");
      setName(''); setEmail(''); setMessage('');
    } catch (err) {
      toast.error('Could not send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen th-hero-radial">
      <Seo
        title="Contact Us | Thirrsty.com"
        description="Get in touch with the Thirrsty.com team. Call us at (410) 504-7466 or send us a message. We'd love to hear from you."
        path="/contact"
      />
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="contact-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Contact Us</h1>
          <div className="ml-auto"><Logo size={96} withText={false} asDownloadTrigger /></div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-6">
        <GlassCard className="p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F6B24A]/15 text-[#F6B24A]">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-white/60">Owner</p>
              <p className="font-display text-xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid="contact-company-name">Banks Class LLC</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3">
            <a href="tel:+14105047466" className="flex items-center gap-3 rounded-xl border border-[#F6B24A]/30 bg-[#F6B24A]/10 p-3 hover:bg-[#F6B24A]/15" data-testid="contact-phone-link">
              <Phone className="h-5 w-5 text-[#F6B24A]" />
              <div>
                <p className="text-xs uppercase tracking-wider text-[#F6B24A]">Phone</p>
                <p className="font-mono text-base text-white" data-testid="contact-phone">(410) 504-7466</p>
              </div>
            </a>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <h2 className="font-display text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Send us a message</h2>
          <p className="mt-1 text-sm text-white/70">Tell us about your venue, partnership inquiries, or feedback.</p>
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div>
              <Label htmlFor="c-name" className="text-white/80">Name</Label>
              <Input id="c-name" required value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/12 text-white placeholder:text-white/40" data-testid="contact-name-input" />
            </div>
            <div>
              <Label htmlFor="c-email" className="text-white/80">Email</Label>
              <Input id="c-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/5 border-white/12 text-white placeholder:text-white/40" data-testid="contact-email-input" />
            </div>
            <div>
              <Label htmlFor="c-msg" className="text-white/80">Message</Label>
              <Textarea id="c-msg" required minLength={2} rows={5} value={message} onChange={(e) => setMessage(e.target.value)} className="bg-white/5 border-white/12 text-white placeholder:text-white/40" data-testid="contact-message-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="contact-us-submit-button">
              <Send className="mr-2 h-4 w-4" /> {loading ? 'Sending…' : 'Send'}
            </Button>
          </form>
        </GlassCard>

        <p className="text-center text-xs text-white/40">
          © Banks Class LLC · Thirrsty.com — 21+ where alcohol is served.
        </p>
      </main>
    </div>
  );
}
